import { useTranslation } from 'react-i18next'
import { Kpi, KpiRow } from './ui/Kpi'
import { formatLongDate, monthLabels } from '../lib/months'
import { useMoney } from '../hooks/useMoney'
import type { ExcessMonth } from '../lib/types'

/** The Status subsection of the Excess Volume card — the prototype's four
 * 17px KPIs (`renderExcess`/`renderAgtExcess`) and `excessMonthsTable`
 * (subscription-portal.html), fed by `GET /orders/overage-summary` (MS-821).
 *
 * Every figure is the server's: the read-time billing period, one row per
 * calendar month the period touches (four for a mid-month anchor), each row's
 * cost rounded up per started 1,000 at its stored rate, and the KPI cost the
 * sum of those rows (ARCHITECTURE_DECISIONS.md §44, OQ-13). The prototype's
 * weighted split of the period total is illustrative by its own comment and
 * is not ported. Money is the contract currency with two decimals (OQ-14). */
export function ExcessBreakdown({
  periodStart,
  periodEnd,
  excessVolume,
  excessCost,
  months,
}: Readonly<{
  periodStart: string | null
  periodEnd: string | null
  excessVolume: number
  excessCost: number
  months: readonly ExcessMonth[]
}>) {
  const { t, i18n } = useTranslation()
  const money = useMoney()
  const hasPeriod = periodStart !== null && periodEnd !== null
  const dash = t('common.dash')
  const cell = 'p-3 border-b border-[var(--border-light)] align-middle'
  const head =
    'font-semibold text-[11px] uppercase tracking-[.5px] text-[var(--text-tertiary)] px-3 py-2.5 border-b border-[var(--border)]'

  return (
    <div>
      <KpiRow>
        <Kpi
          size="compact"
          label={t('excessVolume.periodStart')}
          value={periodStart ? formatLongDate(periodStart, i18n.language) : null}
        />
        <Kpi
          size="compact"
          label={t('excessVolume.periodEnd')}
          value={periodEnd ? formatLongDate(periodEnd, i18n.language) : null}
        />
        <Kpi
          size="compact"
          tone="danger"
          label={t('excessVolume.volume')}
          value={hasPeriod ? excessVolume.toLocaleString(i18n.language) : null}
        />
        <Kpi
          size="compact"
          tone="danger"
          label={t('excessVolume.cost')}
          value={hasPeriod ? money(excessCost, 2) : null}
        />
      </KpiRow>
      <div className="mt-[18px] overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              <th className={`${head} text-left`}>{t('excessVolume.month')}</th>
              <th className={`${head} text-right`}>{t('excessVolume.volume')}</th>
              <th className={`${head} text-right`}>{t('excessVolume.cost')}</th>
            </tr>
          </thead>
          <tbody>
            {!hasPeriod || months.length === 0 ? (
              <tr data-testid="excess-month-row">
                <td className={`${cell} border-b-0 text-[var(--text-tertiary)]`}>{dash}</td>
                <td className={`${cell} border-b-0 text-right text-[var(--text-tertiary)]`}>{dash}</td>
                <td className={`${cell} border-b-0 text-right text-[var(--text-tertiary)]`}>{dash}</td>
              </tr>
            ) : (
              months.map((m) => {
                const [year, month] = m.month.split('-').map(Number)
                const cost = Number(m.excessCost)
                return (
                  <tr key={m.month} data-testid="excess-month-row" className="[&:last-child>td]:border-b-0">
                    <td className={`${cell} whitespace-nowrap`}>
                      {monthLabels(new Date(year, month - 1, 1), i18n.language).label}
                    </td>
                    <td className={`${cell} text-right tabular-nums`}>
                      {m.excessVolume > 0 ? m.excessVolume.toLocaleString(i18n.language) : dash}
                    </td>
                    <td className={`${cell} text-right tabular-nums`}>
                      {m.excessVolume > 0 ? money(cost, 2) : dash}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
